
import Vue from 'vue';
import { ActivityMessage, Button, Icon } from '@descomplica/vue-components';
import {
  CourseModel,
  ReplyModel,
  QuestionModel,
  AssertionModel,
  UserModel,
  ListModel,
} from '@descomplica/dex-models';
import Result from './Result.vue';
import FloatButton from './FloatButton.vue';
import StatusCircle from './StatusCircle.vue';
import Solutions from './Solutions.vue';
import { arrayContainsArray } from '../helpers/array';
import { getPaddedDisplayNumber } from '@/helpers/string';
import Content from './contents/Content.vue';
import i18n from '../i18n';
import { UserListActivityStatus } from '@/services/UndergradGrades';
import DueDateWarningHelper, { ActivityMessageStatus } from '@/helpers/assessments/dueDateWarning';
import { TranslateResult } from 'vue-i18n';
import Analytics from '@/helpers/analytics';
import { EVENT_LABELS } from '@/helpers/analytics/labels';
import { getCourseCategory } from '@/helpers/category';
import { ANALYTICS_LABELS } from '@/helpers/listItems';

interface Data {
  loadingAnswer: Boolean;
  currentQuestionIndex: number;
  currentAssertionId: number;
  showConfirmationButton: Boolean;
  showResult: Boolean;
  showQuestionSolutions: Boolean;
}

export default Vue.extend({
  components: {
    ActivityMessage,
    Result,
    Button,
    FloatButton,
    StatusCircle,
    Solutions,
    Content,
    Icon,
  },
  props: {
    user: {
      type: Object as Vue.PropType<UserModel>,
      required: true,
    },
    questions: {
      type: Array as Vue.PropType<QuestionModel[]>,
      required: true,
    },
    list: {
      type: Object as Vue.PropType<ListModel>,
      required: true,
    },
    course: {
      type: Object as Vue.PropType<CourseModel>,
      required: true,
    },
    userReplies: {
      type: Array as Vue.PropType<ReplyModel[]>,
      default() {
        return [];
      },
    },
    listStatus: {
      type: Object as Vue.PropType<UserListActivityStatus>,
      default: null,
    },
  },
  data(): Data {
    return {
      loadingAnswer: false,
      currentQuestionIndex: 0,
      currentAssertionId: 0,
      showConfirmationButton: false,
      showResult: false,
      showQuestionSolutions: false,
    };
  },
  computed: {
    currentQuestion(): QuestionModel {
      return this.questions[this.currentQuestionIndex];
    },
    hasAllQuestionsDone(): Boolean {
      const userReplyQuestionIdList = this.userReplies.map((userReply) => userReply.questionId);
      const questionIdList = this.questions.map((question) => question.id);
      return arrayContainsArray(userReplyQuestionIdList, questionIdList);
    },
    userQuestionReply(): ReplyModel | null {
      return this.getUserQuestionReply(this.currentQuestion);
    },
  },
  mounted() {
    if (this.listStatus) this.trackingActivityMessageView();
  },
  updated() {
    if (this.loadingAnswer) {
      const reply = this.getUserQuestionReply(this.currentQuestion);
      if (reply) this.loadingAnswer = false;
    }
  },
  methods: {
    getButtonText() {
      if (this.hasAllQuestionsDone) return i18n.t('checkResult');
      if (this.showConfirmationButton) return i18n.t('confirmAssertion');
      if (this.userQuestionReply) return i18n.t('goToNextQuestion');
      return '';
    },
    onButtonClick() {
      if (this.hasAllQuestionsDone) {
        this.goToResult();
      } else if (this.showConfirmationButton) {
        this.confirmAlternative(this.currentAssertionId);
      } else {
        this.goToNext();
      }
    },
    getPaddedDisplayNumber(n: number) {
      return getPaddedDisplayNumber(n);
    },
    toggleQuestionSolutions(): void {
      this.showQuestionSolutions = !this.showQuestionSolutions;
    },
    goToResult(): void {
      if (!this.showResult) {
        this.toggleShowResult();
      }
    },
    goToNext(): void {
      if (this.currentQuestionIndex < this.questions.length - 1) {
        this.currentQuestionIndex += 1;
      } else {
        this.questions.find((question, index) => {
          if (!this.getUserQuestionReply(question)) {
            this.currentQuestionIndex = index;
            return true;
          }

          return false;
        });
      }
    },
    goToQuestion(index: number): void {
      if (this.showResult) {
        this.toggleShowResult();
      }
      this.currentQuestionIndex = index;
    },
    toggleShowResult(): void {
      this.showResult = !this.showResult;
      this.$emit('showResult', this.showResult);
    },
    selectAlternative(assertionId: number): void {
      if (!this.userQuestionReply) {
        this.currentAssertionId = assertionId;
        this.showConfirmationButton = true;
      }
    },
    confirmAlternative(assertionId: number): void {
      this.loadingAnswer = true;
      if (!this.userQuestionReply) {
        this.showConfirmationButton = false;
        this.$emit('onQuestionSubmit', {
          question: this.currentQuestion,
          assertionId,
        });
      }
    },
    getUserQuestionReply(question: QuestionModel): ReplyModel | null {
      return this.userReplies.find((reply) => reply.questionId === question.id) || null;
    },
    isQuestionCorrect(question: QuestionModel): Boolean {
      const reply = this.getUserQuestionReply(question);
      return !!reply && !!reply.isCorrect();
    },
    getAlternativeClass(assertion: AssertionModel): String {
      if (this.currentAssertionId === assertion.id) {
        return 'question__alternative--selected';
      }

      if (!this.userQuestionReply) return '';

      if (!assertion.correct && this.isUserAnswer(assertion.id)) {
        return 'question__alternative--incorrect';
      }

      if (assertion.correct) {
        return 'question__alternative--correct';
      }

      this.currentAssertionId = 0;
      return 'question__alternative--disabled';
    },
    isUserAnswer(assertionId: number): Boolean {
      if (!this.userQuestionReply) return false;
      return this.userQuestionReply.assertionId === assertionId;
    },
    activityStatus(): ActivityMessageStatus {
      return DueDateWarningHelper.getActivityStatus(this.listStatus.status);
    },
    warningMessage(): TranslateResult {
      return DueDateWarningHelper.getObjectiveWarningMessage(
        this.listStatus.status,
        this.isPastDue(),
        this.listStatus.dueDate,
      );
    },
    isPastDue(): boolean {
      const { answeredAt, dueDate, status } = this.listStatus;
      return DueDateWarningHelper.isPastDue(dueDate, answeredAt, status);
    },
    trackingActivityMessageView(): void {
      Analytics.trackEvent(EVENT_LABELS.activityDeadlineView, {
        course: this.$store.getters.currentCourse.name,
        category: getCourseCategory(this.$store.getters.currentCourse),
        content_type: ANALYTICS_LABELS.question,
        lesson_slug: this.$route.params.listSlug,
        discipline_slug: this.$route.params.classroomSlug,
        activity_status: this.listStatus.status,
      });
    },
  },
});
